import { useEffect, useId, useMemo, useRef } from 'react'
import { useController, useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Modal,
  Button,
  Spinner,
  Select,
  MultiSelect,
  DateInput,
  Input,
  TimeInput,
} from '@/components/ui'
import type { MultiSelectOption } from '@/components/ui'
import {
  useShiftTemplates,
  useBulkAssignShifts,
  useRecentCustomShiftSlots,
} from '../hooks/useShifts'
import { useCompanyMembers } from '@/features/companies/hooks/useCompanies'
import { getMemberId, getMemberLabel, getMemberSubline } from '@/features/companies/utils/memberLabel'
import { getDatesInRange } from '../utils/dateRange'
import type { MeCompany } from '@/types/company.types'
import { customShiftSlotBaseSchema, customShiftSlotSchema } from '../schemas/shift.schemas'

const formSchema = z
  .object({
    companyId: z.string().min(1, 'Company is required'),
    shiftId: z.string().optional(),
    userIds: z.array(z.string()).min(1, 'Select at least one user'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    customSlot: customShiftSlotBaseSchema.partial().optional().default({}),
  })
  .superRefine((data, ctx) => {
    const slot = customShiftSlotSchema.safeParse(data.customSlot ?? {})
    if (!slot.success) {
      slot.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['customSlot', ...issue.path],
        })
      })
    }
  })

type BulkAssignShiftFormData = z.infer<typeof formSchema>

export interface BulkAssignModalProps {
  companies: MeCompany[]
  defaultCompanyId?: string
  open: boolean
  onClose: () => void
  startDate: string
  endDate: string
}

export function BulkAssignModal({
  companies,
  defaultCompanyId,
  open,
  onClose,
  startDate,
  endDate,
}: BulkAssignModalProps): React.ReactElement {
  const formId = useId()
  const bulkAssign = useBulkAssignShifts()

  const initialCompanyId =
    defaultCompanyId && companies.some((c) => c._id === defaultCompanyId)
      ? defaultCompanyId
      : (companies[0]?._id ?? '')

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    control,
    formState: { errors },
    clearErrors,
  } = useForm<BulkAssignShiftFormData>({
    resolver: zodResolver(formSchema) as Resolver<BulkAssignShiftFormData>,
    shouldUnregister: false,
    defaultValues: {
      companyId: initialCompanyId,
      shiftId: '',
      userIds: [],
      startDate,
      endDate,
      customSlot: {
        name: '',
        startTime: '',
        endTime: '',
        gracePeriodMinutes: 0,
        color: '',
        description: '',
      },
    } as BulkAssignShiftFormData,
  })
  const { field: companyIdField } = useController({
    name: 'companyId',
    control,
  })
  const { field: shiftIdField } = useController({
    name: 'shiftId',
    control,
  })
  const { field: userIdsField } = useController({
    name: 'userIds',
    control,
  })
  const { field: startDateField } = useController({
    name: 'startDate',
    control,
  })
  const { field: endDateField } = useController({
    name: 'endDate',
    control,
  })
  const { field: customSlotNameField } = useController({
    name: 'customSlot.name',
    control,
  })
  const { field: customStartTimeField } = useController({
    name: 'customSlot.startTime',
    control,
  })
  const { field: customEndTimeField } = useController({
    name: 'customSlot.endTime',
    control,
  })

  const watchedCompanyId = companyIdField.value ?? ''
  const watchedShiftId = shiftIdField.value ?? ''
  const watchedUserIds = userIdsField.value ?? []
  const watchedStart = startDateField.value ?? ''
  const watchedEnd = endDateField.value ?? ''
  const customSlotName = customSlotNameField.value ?? ''
  const customStartTime = customStartTimeField.value ?? ''
  const customEndTime = customEndTimeField.value ?? ''

  const { data: shiftTemplates = [], isLoading: shiftsLoading } = useShiftTemplates(
    open ? watchedCompanyId || null : null
  )
  const { data: recentCustomSlots } = useRecentCustomShiftSlots(watchedCompanyId || null, {
    enabled: !!watchedCompanyId && open,
  })
  const { data: membersPage } = useCompanyMembers(
    open && watchedCompanyId ? watchedCompanyId : null,
    { limit: 100, page: 1 },
  )

  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c._id, label: c.name })),
    [companies]
  )
  const shiftOptions = useMemo(
    () =>
      shiftTemplates.map((s) => ({
        value: s._id,
        label: `${s.name} (${s.startTime} - ${s.endTime})`,
      })),
    [shiftTemplates]
  )
  const userOptions: MultiSelectOption[] = useMemo(
    () =>
      (membersPage?.data ?? []).map((member) => ({
        value: getMemberId(member),
        label: getMemberLabel(member),
        description: getMemberSubline(member),
      })),
    [membersPage?.data]
  )

  const lastOpenRef = useRef(false)
  const resetFormValues = (): void => {
    reset({
      companyId: initialCompanyId,
      shiftId: '',
      userIds: [],
      startDate,
      endDate,
      customSlot: {
        name: '',
        startTime: '',
        endTime: '',
        gracePeriodMinutes: 0,
        color: '',
        description: '',
      },
    } as BulkAssignShiftFormData)
    clearErrors()
  }

  useEffect(() => {
    if (open && !lastOpenRef.current) {
      reset({
        companyId: initialCompanyId,
        shiftId: '',
        userIds: [],
        startDate,
        endDate,
        customSlot: {
          name: '',
          startTime: '',
          endTime: '',
          gracePeriodMinutes: 0,
          color: '',
          description: '',
        },
      } as BulkAssignShiftFormData)
    }
    lastOpenRef.current = open
  }, [open, initialCompanyId, startDate, endDate, reset])

  const lastCompanyRef = useRef(initialCompanyId)
  useEffect(() => {
    if (watchedCompanyId !== lastCompanyRef.current) {
      lastCompanyRef.current = watchedCompanyId
      setValue('shiftId', '')
      setValue('userIds', [])
    }
  }, [watchedCompanyId, setValue])

  // Cross-midnight is allowed: endTime <= startTime now means "ends next day".

  const dateRange = useMemo(
    () => getDatesInRange(watchedStart, watchedEnd),
    [watchedStart, watchedEnd]
  )

  const applyTemplate = (shiftId: string): void => {
    setValue('shiftId', shiftId)
    const shift = shiftTemplates.find((s) => s._id === shiftId)
    if (!shift) return

    setValue('customSlot.name', shift.name, { shouldValidate: true })
    setValue('customSlot.startTime', shift.startTime, { shouldValidate: true })
    setValue('customSlot.endTime', shift.endTime, { shouldValidate: true })
    setValue('customSlot.gracePeriodMinutes', shift.gracePeriodMinutes ?? 0, {
      shouldValidate: true,
    })
    setValue('customSlot.breakAllowances', shift.breakAllowances ?? [], {
      shouldValidate: true,
    })
    setValue('customSlot.color', shift.color ?? '')
    setValue('customSlot.description', shift.description ?? '')
    clearErrors(['customSlot', 'shiftId'])
  }

  const applyRecentSlot = (slot: NonNullable<typeof recentCustomSlots>[number]): void => {
    setValue('shiftId', '')
    setValue('customSlot.name', slot.name, { shouldValidate: true })
    setValue('customSlot.startTime', slot.startTime, { shouldValidate: true })
    setValue('customSlot.endTime', slot.endTime, { shouldValidate: true })
    setValue('customSlot.gracePeriodMinutes', slot.gracePeriodMinutes ?? 0, {
      shouldValidate: true,
    })
    setValue('customSlot.color', slot.color ?? '')
    setValue('customSlot.description', slot.description ?? '')
    clearErrors(['customSlot', 'shiftId'])
  }

  const onSubmit = (data: BulkAssignShiftFormData): void => {
    const dates = getDatesInRange(data.startDate, data.endDate)
    if (dates.length === 0) return
    bulkAssign.mutate(
      {
        companyId: data.companyId,
        userNames: Object.fromEntries(userOptions.map((option) => [option.value, option.label])),
        payload: {
          userIds: data.userIds,
          dates,
          customSlot: {
            name: data.customSlot.name ?? '',
            startTime: data.customSlot.startTime ?? '',
            endTime: data.customSlot.endTime ?? '',
            gracePeriodMinutes: Number(data.customSlot.gracePeriodMinutes ?? 0),
            breakAllowances: data.customSlot.breakAllowances ?? [],
            color: data.customSlot.color || undefined,
            description: data.customSlot.description || undefined,
          },
        },
      },
      {
        onSuccess: () => {
          reset()
          onClose()
        },
      }
    )
  }

  if (!open) return <></>

  const lockedCompany = companies.length === 1

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign shifts"
      className="max-w-3xl"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={resetFormValues}>
            Reset
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form={formId}
            type="submit"
            variant="primary"
            aria-busy={bulkAssign.isPending}
            disabled={!watchedCompanyId || watchedUserIds.length === 0 || dateRange.length === 0 || !customSlotName || !customStartTime || !customEndTime} loading={bulkAssign.isPending}
          >
            {bulkAssign.isPending ? 'Assigning shifts…' : 'Assign'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Company
          </label>
          {lockedCompany ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-100">
              {companies[0]?.name}
            </div>
          ) : (
            <Select
              options={[{ value: '', label: 'Select a company' }, ...companyOptions]}
              value={watchedCompanyId}
              onChange={companyIdField.onChange}
            />
          )}
          {errors.companyId && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {errors.companyId.message}
            </p>
          )}
        </div>

        <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Assignment slot
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Use a saved template or enter a one-off custom time slot.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Use template
              </label>
              {shiftsLoading ? (
                <div className="flex h-9 items-center px-2 text-sm text-gray-500">
                  <Spinner size="sm" className="mr-2" /> Loading shifts…
                </div>
              ) : (
                <Select
                  options={[{ value: '', label: 'Start from scratch' }, ...shiftOptions]}
                  value={watchedShiftId}
                  onChange={applyTemplate}
                  clearable={false}
                />
              )}
              {errors.shiftId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.shiftId.message}
                </p>
              )}
            </div>

            {!!recentCustomSlots?.length && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
                  Recent custom slots
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {recentCustomSlots.slice(0, 4).map((slot) => (
                    <button
                      key={`${slot.name}-${slot.startTime}-${slot.endTime}`}
                      type="button"
                      onClick={() => applyRecentSlot(slot)}
                      className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left transition hover:border-amber-300 hover:bg-amber-100 dark:border-amber-800/70 dark:bg-amber-950/40 dark:hover:bg-amber-900/40"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate text-sm font-medium text-amber-950 dark:text-amber-100">
                          {slot.name}
                        </span>
                        <span className="shrink-0 font-mono text-xs text-amber-700 dark:text-amber-300">
                          {slot.startTime} - {slot.endTime}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Slot name"
                {...register('customSlot.name')}
                error={errors.customSlot?.name?.message}
                placeholder="e.g. Weekend support"
              />
              <Input
                label="Grace minutes"
                type="number"
                min={0}
                {...register('customSlot.gracePeriodMinutes', { valueAsNumber: true })}
                error={errors.customSlot?.gracePeriodMinutes?.message}
              />
              <TimeInput
                label="Start time"
                value={customStartTime}
                onChange={customStartTimeField.onChange}
                error={errors.customSlot?.startTime?.message}
              />
              <TimeInput
                label="End time"
                value={customEndTime}
                onChange={customEndTimeField.onChange}
                error={errors.customSlot?.endTime?.message}
              />
              <BulkCustomSlotDurationHint startTime={customStartTime} endTime={customEndTime} />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Color</label>
              <input
                type="color"
                {...register('customSlot.color')}
                className="h-8 w-12 cursor-pointer rounded border border-gray-300 bg-transparent p-0.5 dark:border-gray-600"
                aria-label="Slot color"
              />
            </div>

            <Input
              label="Description"
              {...register('customSlot.description')}
              error={errors.customSlot?.description?.message}
              placeholder="Optional note for this slot"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Staff members
          </label>
          <MultiSelect
            options={userOptions}
            value={watchedUserIds}
            onChange={userIdsField.onChange}
            placeholder="Add staff…"
          />
          {errors.userIds && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.userIds.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Start date
            </label>
            <DateInput value={watchedStart} onChange={startDateField.onChange} max={watchedEnd} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              End date
            </label>
            <DateInput value={watchedEnd} onChange={endDateField.onChange} min={watchedStart} />
          </div>
        </div>
        {dateRange.length > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {dateRange.length} day(s) — {dateRange[0]} to {dateRange[dateRange.length - 1]}
          </p>
        )}
        {dateRange.length === 0 && watchedStart && watchedEnd && (
          <p className="text-xs text-red-600 dark:text-red-400">
            End date must be on or after start date.
          </p>
        )}
      </form>
    </Modal>
  )
}

BulkAssignModal.displayName = 'BulkAssignModal'

const HH_MM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

function BulkCustomSlotDurationHint({
  startTime,
  endTime,
}: {
  startTime?: string
  endTime?: string
}): React.ReactElement | null {
  if (!startTime || !endTime) return null
  if (!HH_MM_RE.test(startTime) || !HH_MM_RE.test(endTime)) return null
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const startMin = sh * 60 + sm
  let endMin = eh * 60 + em
  const crossesMidnight = endMin <= startMin
  if (crossesMidnight) endMin += 1440
  const duration = endMin - startMin
  if (duration === 0) return null
  const h = Math.floor(duration / 60)
  const m = duration % 60
  const durationText = m === 0 ? `${h}h` : `${h}h ${m}m`
  return (
    <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
      Duration: <span className="font-medium text-gray-700 dark:text-gray-300">{durationText}</span>
      {crossesMidnight && (
        <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          Ends next day
        </span>
      )}
    </p>
  )
}
